<script setup>
import {
  ref,
  reactive,
  onMounted,
  watchEffect,
  computed,
  watch,
  toRaw,
  nextTick,
} from "vue";
import { useCurrentMenuStore } from "../../stores/current-menu";

//import Button from "../Button.vue";

import MenuItemVenue from "./MenuItemVenue";
import MenuItemWine from "./MenuItemWine";
import ConfirmModalVue from "../modals/ConfirmModal.vue";
import MenuItemModal from "../modals/MenuItemModal.vue";

const emit = defineEmits(["rerender"]);

const props = defineProps({
  item: {
    required: true,
  },
  itemIndex: {
    required: true,
  },
  categoryIndex: {
    required: true,
  },
  subcategoryIndex: {
    required: true,
  },
  type: {
    required: true,
  },
});

const item = reactive(props.item);

const currentMenuStore = useCurrentMenuStore();

const collapsed = ref(true);

const title = ref(false);
const description = ref(false);

const addAddons = ref(false);
const addOptions = ref(false);

const addon = ref({
  name: "",
  price: 0,
  calories: 0,
});

const all_allergens = [
  'celery',
  'gluten',
  'crustaceans',
  'egg',
  'fish',
  'lupin',
  'milk',
  'molluscs',
  'mustard',
  'nuts',
  'peanuts',
  'sesame',
  'soya',
  'sulphur_dioxide'
];

const all_suitabilities = [
  'halal',
  'kosher',
  'vegetarian',
  'vegan',
  'gluten_free',
  'spicy'
]

const option = ref(null);

const originalItem = ref({});

const currentTab = computed(function () {
  switch (currentMenuStore.currentTab) {
    case "food":
      return "Dish";
    case "drink":
      return "Drink";
    default:
      return "";
  }
});

const originalAllergenSuitability = ref({});

watch(
  item,
  async function () {
    originalItem.value = currentMenuStore.menuItems.filter(
      (menuItem) => menuItem.product_id == item.product_id
    )[0];

    if (!originalItem.value || !item) return;

    calculateDefaultAllergenSuitability();

    await nextTick();

    // the below if statements are required when the category and subcategories are deleted to stop it from breaking
    if (props.type == currentMenuStore.currentTab) {
      // if statement to check if current category index exists
      if (props.categoryIndex in currentMenuStore[currentMenuStore.currentTab]) {
        // if statement to check if current subcategory index exists
        if (
          props.subcategoryIndex in
          currentMenuStore[currentMenuStore.currentTab][props.categoryIndex].contents
        ) {
          currentMenuStore[currentMenuStore.currentTab][props.categoryIndex].contents[
            props.subcategoryIndex
          ].contents[props.itemIndex] = toRaw(item);
        }
      }
    }
  },
  { deep: true }
);

watch(collapsed, () => {
  originalItem.value = currentMenuStore.menuItems.filter(
    (menuItem) => menuItem.product_id == item.product_id
  )[0];

  if (!originalItem.value || !item) return;

  calculateDefaultAllergenSuitability();
});

onMounted(() => {
  if (item) {
    item.options = item.options || [];

    // Create object for items to be ignored by Tevalis.
    // This is to stop Tevalis from changing allergens + suitability that has been overriden by Parogon.
    item.no_sync = item.no_sync || [];

    item.isWine = item.isWine ? item.isWine : false;

    if (!item.measure) {
      let itemReference = currentMenuStore.menuItems.filter((menuItem) => {
        if (menuItem.product_id == item.product_id) {
          return true;
        }
      })[0];

      if (itemReference.measure) {
        item.measure = itemReference.measure;
        item.measure_id = itemReference.measure_id;
      }
    }

    originalItem.value = currentMenuStore.menuItems.filter((menuItem) => {
      if (menuItem.product_id == item.product_id && menuItem.measure == item.measure) {
        return true;
      }
    })[0];
  }

  document.addEventListener("mousedown", function (e) {
    if (
      description.value &&
      e.target.id !==
      `description-${item.id + props.itemIndex + props.categoryIndex + props.subcategoryIndex
      }`
    ) {
      description.value = false;
    }

    if (
      title.value &&
      e.target.id !==
      `title-${item.id + props.itemIndex + props.categoryIndex + props.subcategoryIndex
      }` &&
      item.item
    ) {
      title.value = false;

      // If item has no title, set it to the default title
      if (!item.item.trim()) {
        item.item = currentMenuStore.menuItems.filter(
          (item) => item.id === item.id
        )[0].item;
      }
    }
  });
});

function calculateAllergenSuitability() {
  originalItem.value = currentMenuStore.menuItems.filter((menuItem) => {
    if (menuItem.product_id == item.product_id && menuItem.measure == item.measure) {
      return true;
    }
  })[0];

  for (const allergen of all_allergens) {
    if (!isOverridden(`allergen_${allergen}`)) {
      item[`allergen_${allergen}`] = originalItem.value[`allergen_${allergen}`]
    }
  }

  for (const suitability of all_suitabilities) {
    if (!isOverridden(`nutrition_${suitability}`)) {
      item[`nutrition_${suitability}`] = originalItem.value[`nutrition_${suitability}`]
    }
  }

  if (item.hasOwnProperty("options")) {
    item.options.forEach((option) => {
      setAllergenSuitability(option);
    });
  }

  calculateDefaultAllergenSuitability();
}

function calculateDefaultAllergenSuitability() {

  for (const allergen of all_allergens) {
    originalAllergenSuitability.value[`allergen_${allergen}`] = originalItem.value[`allergen_${allergen}`];
  }

  for (const suitability of all_suitabilities) {
    originalAllergenSuitability.value[`nutrition_${suitability}`] = originalItem.value[`nutrition_${suitability}`];
  }

  if (item.options) {
    item.options.forEach((optionItem) => {
      for (const allergen of all_allergens) {
        originalAllergenSuitability.value[`allergen_${allergen}`] = optionItem[`allergen_${allergen}`]
          ? optionItem[`allergen_${allergen}`]
          : originalAllergenSuitability.value[`allergen_${allergen}`];
      }

      for (const suitability of all_suitabilities) {
        originalAllergenSuitability.value[`nutrition_${suitability}`] = optionItem[`nutrition_${suitability}`]
          ? optionItem[`nutrition_${suitability}`]
          : originalAllergenSuitability.value[`nutrition_${suitability}`];
      }
    });
  }

}

function setAllergenSuitability(modifyItem) {
  if (originalItem.value) {
    for (const allergen of all_allergens) {
      if (!isOverridden(`allergen_${allergen}`)) {
        item[`allergen_${allergen}`] = modifyItem[`allergen_${allergen}`]
          ? modifyItem[`allergen_${allergen}`]
          : item[`allergen_${allergen}`];
      }
    }

    for (const suitability of all_suitabilities) {
      if (!isOverridden(`nutrition_${suitability}`)) {
        item[`nutrition_${suitability}`] = modifyItem[`nutrition_${suitability}`]
          ? modifyItem[`nutrition_${suitability}`]
          : item[`nutrition_${suitability}`];
      }
    }
  }
}

// Remove dish option
function removeOption(index) {
  item.options.splice(index, 1);
}

watch(
  () => item.options,
  () => {
    calculateAllergenSuitability();
  },
  { deep: true }
);

function addAddon() {
  if (!("addons" in item)) {
    item.addons = [];
  }
  if (addon.value.name.length > 0) {
    item.addons.push(addon.value);
    addon.value = {
      name: "",
      price: 0,
    };
  }
}

function addOption(option_item) {
  item.options.push(option_item);
  return;
}

watchEffect(() => {
  if (currentMenuStore) getPrices();
});

function getPrices() {
  const pricesJSON = JSON.parse(item.prices);

  let sites = currentMenuStore.sortedSites;

  sites = sites.map((site) => {
    let active = true;
    let price = 0;
    let overridden = false;
    if (pricesJSON) {
      price = pricesJSON[site.id];
    }

    if (item.calculatedPrices) {
      let index = item.calculatedPrices.findIndex((_site) => _site.id == site.id);
      if (item.calculatedPrices[index]) {
        active = item.calculatedPrices[index].active;
        price = item.calculatedPrices[index].price;
        overridden = item.calculatedPrices[index].overridden ? true : false;
      } else {
        active = true;
      }
    } else {
      active = true;
    }

    return {
      active: active,
      id: site.id,
      name: site.name,
      price: price,
      overridden: overridden,
    };
  });

  item.calculatedPrices = sites;
}

// Toggles whether the particular allergen/suitability passed to the function is set as overridden or not
function toggleOverride(override) {
  // Check if particular allergen/suitability is set to not sync from Tevalis
  const index = item.no_sync.indexOf(override);
  if (index > -1) { // only splice array when item is found
    item.no_sync.splice(index, 1); // 2nd parameter means remove one item only

    // Reset back to Tevalis value
    item[override] = originalAllergenSuitability.value[override];
  } else {
    item.no_sync.push(override);
  }
}

function isOverridden(override) {
  const index = item.no_sync.indexOf(override);

  if (index > -1) return true;
  else return false;
}

// Deletes menu item
function removeItem() {
  if (props.itemIndex > -1) {
    collapsed.value = true;
    currentMenuStore[currentMenuStore.currentTab][props.categoryIndex].contents[
      props.subcategoryIndex
    ].contents.splice(props.itemIndex, 1);
    emit("rerender");
  }
}
</script>

<template>
  <div class="menu-item">
    <div class="menu-item__header">
      <div class="menu-item__header--left">
        <div class="handle">
          <svg-vue icon="handle"></svg-vue>
        </div>
        <input type="checkbox" v-model="item.active" id="" />
        <p class="can-edit" @click="title = !title" v-if="!title"
          :id="`title-${item.id + itemIndex + categoryIndex + subcategoryIndex}`">
          {{ item.item }}
        </p>
        <input type="text" v-model="item.item" v-if="title"
          :id="`title-${item.id + itemIndex + categoryIndex + subcategoryIndex}`" />
      </div>
      <div>{{ item.product_id }}</div>
      <div class="menu-item__header--right">
        <div class="toggle-collapse" @click="collapsed = !collapsed" :class="{ collapsed }">
          <svg-vue icon="arrow-toggle"></svg-vue>
        </div>
      </div>
    </div>

    <div class="menu-item__content" v-show="!collapsed">
      <div class="menu-item__content--top">
        <div>
          <p class="title">Description</p>
          <textarea class="description-input" v-if="description"
            :id="`description-${item.id + itemIndex + categoryIndex + subcategoryIndex}`" ref="description"
            v-model="item.description">{{ item.description }}</textarea>
          <p class="can-edit description" :id="`description-${item.id + itemIndex + categoryIndex + subcategoryIndex}`"
            v-else @click="description = !description">
            {{ item.description ? item.description : "Click to add description" }}
          </p>

          <p class="calories">Calories: {{ item.nutrition_calories_kcal }}kcal</p>
          <p class="measure">Measure: {{ item.measure }}</p>
        </div>
        <div>
          <p class="title">Addons</p>
          <ul class="addons">
            <li v-for="(addons, index) in item.addons">
              <p>{{ addons.name }}</p>
              <p v-if="addons.price">£{{ addons.price.toFixed(2) }}</p>
              <p>
                <span v-if="addons.calories">{{ addons.calories }}kcal</span>
              </p>
              <div class="remove">
                <svg-vue icon="cross" @click="item.addons.splice(index, 1)"></svg-vue>
              </div>
            </li>
          </ul>
          <div class="add" v-if="!addAddons" @click="addAddons = !addAddons">
            <div class="add__icon">
              <svg-vue icon="plus"></svg-vue>
            </div>
            <p>Add new</p>
          </div>
          <div v-else class="addons__add">
            <div>
              <label>Name</label>
              <input type="text" v-model="addon.name" placeholder="e.g. Chips" />
            </div>
            <div>
              <label>Price</label>
              <input type="number" v-model="addon.price" placeholder="Price" />
            </div>
            <div>
              <label>Calories</label>
              <input type="number" v-model="addon.calories" placeholder="Calories" />
            </div>
            <div @click="addAddon" class="link-add button">Add</div>
          </div>
        </div>
      </div>
      <div class="menu-item__content--bottom">
        <p class="title">Add dish options for allergens &amp; suitability</p>
        <ul class="options">
          <li v-for="(options, index) in item.options">
            <p>{{ options.item }}</p>
            <p>{{ options.product_id }}</p>
            <div class="remove" @click="removeOption(index)">
              <svg-vue icon="cross"></svg-vue>
            </div>
          </li>
        </ul>

        <MenuItemModal :items="currentMenuStore.menuItems" :category="currentMenuStore.currentTab" :is_option="true"
          @addOption="addOption">
          Add Option
        </MenuItemModal>

        <div class="item-info" v-if="originalAllergenSuitability && item.no_sync">
          <div>
            <p class="title">Allergens</p>
            <div class="item-info__head">
              <p>Allergen</p>
              <p>Sync</p>
            </div>
            <ul class="prices">
              <li :class="{
                overridden:
                  originalAllergenSuitability.allergen_celery != item.allergen_celery,
              }">
                <input type="checkbox" v-model="item.allergen_celery" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('allergen_celery')" />
                <span>Celery</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('allergen_celery')" /><label
                    @click="toggleOverride('allergen_celery')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.allergen_gluten != item.allergen_gluten,
              }">
                <input type="checkbox" v-model="item.allergen_gluten" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('allergen_gluten')" />
                <span>Gluten</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('allergen_gluten')" /><label
                    @click="toggleOverride('allergen_gluten')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.allergen_crustaceans !=
                  item.allergen_crustaceans,
              }">
                <input type="checkbox" v-model="item.allergen_crustaceans" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('allergen_crustaceans')" />
                <span>Crustaceans</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('allergen_crustaceans')" /><label
                    @click="toggleOverride('allergen_crustaceans')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.allergen_egg != item.allergen_egg,
              }">
                <input type="checkbox" v-model="item.allergen_egg" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('allergen_egg')" />
                <span>Egg</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('allergen_egg')" /><label
                    @click="toggleOverride('allergen_egg')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.allergen_fish != item.allergen_fish,
              }">
                <input type="checkbox" v-model="item.allergen_fish" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('allergen_fish')" />
                <span>Fish</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('allergen_fish')" /><label
                    @click="toggleOverride('allergen_fish')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.allergen_lupin != item.allergen_lupin,
              }">
                <input type="checkbox" v-model="item.allergen_lupin" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('allergen_lupin')" />
                <span>Lupin</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('allergen_lupin')" /><label
                    @click="toggleOverride('allergen_lupin')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.allergen_milk != item.allergen_milk,
              }">
                <input type="checkbox" v-model="item.allergen_milk" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('allergen_milk')" />
                <span>Milk</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('allergen_milk')" /><label
                    @click="toggleOverride('allergen_milk')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.allergen_molluscs !=
                  item.allergen_molluscs,
              }">
                <input type="checkbox" v-model="item.allergen_molluscs" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('allergen_molluscs')" />
                <span>Molluscs</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('allergen_molluscs')" /><label
                    @click="toggleOverride('allergen_molluscs')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.allergen_mustard != item.allergen_mustard,
              }">
                <input type="checkbox" v-model="item.allergen_mustard" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('allergen_mustard')" />
                <span>Mustard</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('allergen_mustard')" /><label
                    @click="toggleOverride('allergen_mustard')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.allergen_nuts != item.allergen_nuts,
              }">
                <input type="checkbox" v-model="item.allergen_nuts" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('allergen_nuts')" />
                <span>Nuts</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('allergen_nuts')" /><label
                    @click="toggleOverride('allergen_nuts')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.allergen_peanuts != item.allergen_peanuts,
              }">
                <input type="checkbox" v-model="item.allergen_peanuts" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('allergen_peanuts')" />
                <span>Peanuts</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('allergen_peanuts')" /><label
                    @click="toggleOverride('allergen_peanuts')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.allergen_sesame != item.allergen_sesame,
              }">
                <input type="checkbox" v-model="item.allergen_sesame" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('allergen_sesame')" />
                <span>Sesame</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('allergen_sesame')" /><label
                    @click="toggleOverride('allergen_sesame')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.allergen_soya != item.allergen_soya,
              }">
                <input type="checkbox" v-model="item.allergen_soya" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('allergen_soya')" />
                <span>Soya</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('allergen_soya')" /><label
                    @click="toggleOverride('allergen_soya')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.allergen_sulphur_dioxide !=
                  item.allergen_sulphur_dioxide,
              }">
                <input type="checkbox" v-model="item.allergen_sulphur_dioxide" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('allergen_sulphur_dioxide')" />
                <span>Sulphur Dioxide</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('allergen_sulphur_dioxide')" /><label
                    @click="toggleOverride('allergen_sulphur_dioxide')">Toggle</label>
                </div>
              </li>
            </ul>
          </div>

          <div>
            <p class="title">Suitability</p>
            <div class="item-info__head">
              <p>Suitability</p>
              <p>Sync</p>
            </div>
            <ul class="prices">
              <li :class="{
                overridden:
                  originalAllergenSuitability.nutrition_halal != item.nutrition_halal,
              }">
                <input type="checkbox" v-model="item.nutrition_halal" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('nutrition_halal')" />
                <span>Halal</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('nutrition_halal')" /><label
                    @click="toggleOverride('nutrition_halal')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.nutrition_kosher != item.nutrition_kosher,
              }">
                <input type="checkbox" v-model="item.nutrition_kosher" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('nutrition_kosher')" />
                <span>Kosher</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('nutrition_kosher')" /><label
                    @click="toggleOverride('nutrition_kosher')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.nutrition_vegetarian !=
                  item.nutrition_vegetarian,
              }">
                <input type="checkbox" v-model="item.nutrition_vegetarian" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('nutrition_vegetarian')" />
                <span>Vegetarian</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('nutrition_vegetarian')" /><label
                    @click="toggleOverride('nutrition_vegetarian')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.nutrition_vegan != item.nutrition_vegan,
              }">
                <input type="checkbox" v-model="item.nutrition_vegan" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('nutrition_vegan')" />
                <span>Vegan</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('nutrition_vegan')" /><label
                    @click="toggleOverride('nutrition_vegan')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.nutrition_gluten_free !=
                  item.nutrition_gluten_free,
              }">
                <input type="checkbox" v-model="item.nutrition_gluten_free" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('nutrition_gluten_free')" />
                <span>Gluten Free</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('nutrition_gluten_free')" /><label
                    @click="toggleOverride('nutrition_gluten_free')">Toggle</label>
                </div>
              </li>
              <li :class="{
                overridden:
                  originalAllergenSuitability.nutrition_spicy != item.nutrition_spicy,
              }">
                <input type="checkbox" v-model="item.nutrition_spicy" true-value="1" false-value="0"
                  :disabled="!item.no_sync.includes('nutrition_spicy')" />
                <span>Spicy</span>
                <div class="toggle-override">
                  <input type="checkbox" :checked="!item.no_sync.includes('nutrition_spicy')" /><label
                    @click="toggleOverride('nutrition_spicy')">Toggle</label>
                </div>
              </li>
            </ul>
          </div>

          <div class="venues" v-if="!item.isWine">
            <div class="wine-check" v-show="currentMenuStore.currentTab == 'drink'">
              <input type="checkbox" name="is-wine" v-model="item.isWine" />
              <span>Is wine?</span>
            </div>
            <p class="title">Venues</p>
            <ul class="venues__list">
              <li v-for="venue in item.calculatedPrices">
                <MenuItemVenue :item="venue" :currentMenuItem="item.product_id" :measure="item.measure"></MenuItemVenue>
              </li>
            </ul>
          </div>

          <div class="venues venues-wine" v-if="item.isWine">
            <div class="wine-check" v-show="currentMenuStore.currentTab == 'drink'">
              <input type="checkbox" name="is-wine" v-model="item.isWine" />
              <span>Is wine?</span>
            </div>
            <p class="title">Wine prices</p>
            <div>
              <ul v-for="venue in currentMenuStore.currentMenu.sites">
                <MenuItemWine :site="venue" :currentMenuItem="item.id" :categoryIndex="categoryIndex"
                  :subcategoryIndex="subcategoryIndex"></MenuItemWine>
              </ul>
            </div>
          </div>
        </div>

        <div class="button-holder">
          <ConfirmModalVue :btnContent="`Remove ${currentTab}`" :method="removeItem">
            Are you sure you want to delete this menu item?
          </ConfirmModalVue>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "../../../scss/partials/variables";

.wine-check {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.menu-item {
  margin-left: auto;
  width: 98%;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--grid-padding);
    border-bottom: 2px solid var(--grey-lighter);
    background: #fff;

    &--left {
      display: flex;
      align-items: center;
      gap: 20px;
      width: 100%;

      input[type="text"] {
        margin: 0;
        width: 90%;
      }

      input[type="checkbox"] {
        margin-right: 0;
      }
    }

    &--right {}
  }

  .handle {
    display: flex;
    align-items: center;
    cursor: grab;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  &__content {
    .button {
      align-self: end;

      span {
        color: white;
      }
    }

    &--top {
      background-color: var(--grey-dark);
      padding: var(--grid-padding);
      padding-left: calc(var(--grid-padding) + 40px);
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;

      textarea {
        height: 50px;
      }

      .calories {
        margin-top: 20px;
        font-size: 0.8em;
        font-weight: 600;
      }

      .measure {
        font-size: 0.8em;
        font-weight: 600;
      }
    }

    .addons,
    .options {
      display: grid;
      gap: 10px;
      margin-bottom: 10px;

      li {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-right: 25px;

        p:first-child {
          text-align: left;
        }

        .remove {
          padding: 6px;
          background: #fff;
          height: auto;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          right: 0;
          cursor: pointer;
          width: 20px;
          height: 20px;
          border-radius: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      &__add {
        display: grid;
        grid-template-columns: 1fr 1fr auto;
        align-items: center;
        gap: 20px;

        input {
          margin: 0;
        }
      }
    }

    .addons {
      li {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        text-align: right;
      }

      &__add {
        grid-template-columns: 1fr 1fr 1fr;
      }
    }

    &--bottom {
      background-color: var(--grey-darker);
      padding: var(--grid-padding);
      padding-left: calc(var(--grid-padding) + 40px);

      .item-info {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #000;

        &__head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          text-transform: uppercase;
          font-size: 13px;
          letter-spacing: 1px;
          margin-bottom: 5px;
          font-family: var(--secondary-font);
        }

        @media screen and (max-width: $breakpoint-xl) {
          grid-template-columns: repeat(2, 1fr);
        }

        li {
          list-style: none;
          margin-bottom: 5px;
          font-size: 14px;
          display: flex;
          align-items: center;

          &.overridden {
            color: red;
            font-weight: bold;
          }
        }

        .venues {
          @media screen and (max-width: $breakpoint-xl) {
            width: 80%;
          }

          @media screen and (max-width: $breakpoint-sm) {
            width: 100%;
          }

          &__list {
            li {
              display: flex;
              align-items: center;
            }
          }
        }

        .venues-wine {
          width: 500px;

          input[type="text"] {
            padding: 5px;
            margin: 0;
          }

          @media screen and (max-width: $breakpoint-xl) {
            grid-column: span 2;
            width: unset;
          }

          ul {
            &:not(:last-child) {
              margin-bottom: 40px;
            }

            h3 {
              margin-bottom: 5px;
            }

            li {
              display: flex;
              justify-content: space-between;
              gap: 20px;

              input {
                margin-left: auto;
                width: 200px;
              }
            }
          }
        }
      }
    }

    .title {
      font-weight: bold;
      margin-bottom: 20px;
    }

    .description {
      white-space: pre-wrap;
    }
  }

  .add {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    cursor: pointer;

    &:hover {
      .add__icon {
        transform: scale(1.1);
      }
    }

    &__icon {
      width: 20px;
      height: 20px;
      background: #fff;
      border-radius: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 10px;
      transition: 0.3s ease;

      svg {
        width: 8px;
        height: 8px;
      }
    }
  }

  .button-holder {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }
}

.description-input {
  max-width: 500px;
}

@media screen and (max-width: $breakpoint-sm) {
  .menu-item {
    &__content {
      &--top {
        padding: 30px;
        display: block;

        div:last-child {
          margin-top: 20px;
        }
      }

      &--bottom {
        padding: 30px;

        .item-info {
          input[type="checkbox"] {
            margin-right: 5px;
          }
        }
      }

      .options {
        &__add {
          display: block;
        }
      }
    }

    .venues {
      width: 100%;
    }
  }
}

@media screen and (max-width: $breakpoint-lg) {
  .menu-item {
    &__content {
      &--bottom {
        .item-info {
          gap: 0;
          display: block;

          input[type="checkbox"] {
            margin-right: 5px;
          }

          .prices {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            margin-bottom: 20px;
            gap: 0px 10px;

            li {
              width: 100%;
              font-size: 14px;
            }
          }
        }
      }
    }

    .addons {
      &__add {
        display: block;
      }
    }

    .venues {
      width: 80%;

      &__list {
        font-size: 14px;
      }
    }
  }
}


// Toggle
.toggle-override {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;

  input[type=checkbox] {
    height: 0;
    min-height: 0;
    width: 0;
    min-width: 0;
    visibility: hidden;
  }

  label {
    cursor: pointer;
    text-indent: -9999px;
    width: 35px;
    height: 20px;
    background: grey;
    display: block;
    border-radius: 100px;
    position: relative;
    transition: 0.2s background ease;
  }

  label:after {
    content: '';
    position: absolute;
    top: 5px;
    left: 5px;
    width: 11px;
    height: 11px;
    background: #fff;
    border-radius: 45px;
    transition: 0.3s;
  }

  input:checked+label {
    background: var(--green);
  }

  input:checked+label:after {
    left: calc(100% - 5px);
    transform: translateX(-100%);
  }

  label:active:after {
    width: 15px;
  }
}
</style>
